import { getApplicationErrorReport } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementPage/ValidationInventoryService';
import React, { useState } from 'react';
import { LatestRemark, MasterPoolTableFields } from '../../HbtMasterPool/types';
import { MRT_Row } from 'material-react-table';
import { AuthenticationContextType, useAuthenticationContext } from 'Foundation/Authentication';
import moment from 'moment';
import { handleMasterPoolDownload } from '../utils/FileDownLoad';
import { getMasterpoolsDataSet } from '../hooks/MasterPoolDataSetService';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { CmhcFlexIcon, CmhcIcon, CmhcIconSize, Link } from '@cmhc-schl/hearth-ui';
import styles from '../styles.module.scss';
import { useTranslation } from 'react-i18next';
import { config } from '../../../../../../config';
import { showOnDemandDoc } from '../../HbtMasterPoolDetails/components/Flyout/DocumentsAndResourcesFlyout';
import { SharepointDomain } from 'Feature/V2/HbtMasterPool/constants';
import { convertTimestampToLocalDate } from 'Feature/Header/models/types';

interface RowActionsInterface {
  row: MRT_Row<MasterPoolTableFields>;
  handleToastNotification: (icon: string, message: string, desc?: string) => void;
  toggleMasterPoolNumberRemarksDrawer: (newOpen: boolean) => void;
  setSelectedRow: React.Dispatch<
    React.SetStateAction<{
      masterPoolNumber: number | null;
      latestRemark: LatestRemark;
    }>
  >;
}

export const getAppErrorReport = async (
  applicationId: number,
  authenticationContext: AuthenticationContextType,
  requestUrl?: string
) => {
  try {
    const errorReport = await getApplicationErrorReport(
      authenticationContext,
      applicationId,
      requestUrl
    );
    if (!errorReport || (errorReport.statusCode !== 200 && errorReport.status !== 200)) {
      throw new Error();
    }

    const dispo = errorReport.headers['content-disposition'] ?? '';
    const match = dispo.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] ?? 'data.xlsx';

    const url = URL.createObjectURL(errorReport.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

export const RowActions = ({
  row,
  setSelectedRow,
  handleToastNotification,
  toggleMasterPoolNumberRemarksDrawer
}: RowActionsInterface) => {
  const env = encodeURIComponent(`${process.env.REACT_APP_ENV}`);
  const sharepointUrl =
    '/Pricing/2025/20250819173006_82535646_Pricing.xlsx?d=wc079e2a47ebb43f4888d30dae6a91612&csf=1&web=1&e=et4PzI';
  const authenticationContext = useAuthenticationContext();
  const { t } = useTranslation('HbtMasterPool');

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const masterPoolInventoryDetail = config.validationMasterPoolAPi.urls.masterPoolInventoryDetail;
  const handleGetErrorReport = async (
    applicationId: number,
    row: MasterPoolTableFields,
    type?: 'cashierReport'
  ) => {
    try {
      const requestUrl =
        type === 'cashierReport'
          ? masterPoolInventoryDetail + `/${row.masterPoolCmhcAccountNumber}/cashier/report`
          : undefined;
      await getAppErrorReport(applicationId, authenticationContext, requestUrl);
    } catch (error) {
      if (error.error && error.error.errorCode === 'HBT_ERR_1100') {
        handleToastNotification('icon_info_outlined', t('No-Data-Available-Report'), String(' '));
        return;
      }
      handleToastNotification('icon_info_outlined', t('Download-failed'), t('Download-failed-Sub'));
    }
  };

  const handleErrorReport = async (type?: 'cashierReport') => {
    await handleGetErrorReport(row.original.applicationId, row.original, type);
    handleCloseMenu();
  };

  const handleFileDownload = async () => {
    const masterPoolCmhcAccountNumber = row?.original?.masterPoolCmhcAccountNumber?.toString();
    const lenderCode = row?.original.insuredApprovedLenderCode;
    const formattedDate = moment().format('YYYY_MM_DD');
    const fileName = `MasterPoolDatasets_${lenderCode}_${masterPoolCmhcAccountNumber}_${formattedDate}.xlsx`;
    await handleMasterPoolDownload({
      authenticationContext,
      masterPoolCmhcAccountNumber: masterPoolCmhcAccountNumber,
      fileName: fileName,
      getMasterpoolsDataSet,
      onComplete: handleCloseMenu
    });
  };

  const handleAddModifyRemarks = () => {
    toggleMasterPoolNumberRemarksDrawer(true);
    setSelectedRow({
      masterPoolNumber: row.original.masterPoolCmhcAccountNumber,
      latestRemark: row.original.latestRemark ?? ({} as LatestRemark)
    });
  };

  return (
    <Box aria-label={`row-action-${row.id}`} className={styles.loanInventoryActionButton}>
      <Tooltip title="Actions">
        <IconButton onClick={handleOpenMenu}>
          <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />
        </IconButton>
      </Tooltip>
      <Menu
        sx={{
          minWidth: '250px',
          '.MuiMenuItem-root': {
            padding: '12px 16px'
          },
          '.MuiPaper-root': {
            boxShadow: 'unset',
            border: '2px solid #697885',
            width: '262px'
          },
          '.MuiList-padding': {
            padding: '0px !important'
          }
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          className={styles.addModifyRemarks}
          onClick={handleAddModifyRemarks}
          data-testid="add-remark-menu-item"
        >
          <CmhcIcon size={CmhcIconSize.SMALL} type="icon_comment_add" />
          <span>{t('Masterpool-Flyout-Add-Remark')}</span>
        </MenuItem>
        <Box className={styles.onRowActionItemsHeader}>{t('Available-Files')}</Box>
        <Box className={styles.onRowActionBody}>
          <MenuItem
            onClick={handleFileDownload}
            className={styles.menuItem}
            data-testid="download-dataset-menu-item"
          >
            <CmhcFlexIcon width={32} height={32} type="icon_dataset" />
            <span>{t('Master-Pool-Datasets')}</span>
          </MenuItem>
        </Box>
        {showOnDemandDoc(row.original.masterPoolAccountStatusCode, row.original.requestType) && (
          <>
            <Box className={styles.onRowActionItemsHeader}>{t('On-Documents')}</Box>
            <Box className={styles.onRowActionBody}>
              <MenuItem
                onClick={() => handleErrorReport('cashierReport')}
                className={styles.menuItem}
                data-testid="cashier-report-menu-item"
              >
                <CmhcFlexIcon width={32} height={32} type="icon_document" />
                <span>{t('Cashier-Report')}</span>
              </MenuItem>
            </Box>
          </>
        )}
      </Menu>
    </Box>
  );
};
